import React, { useState, useMemo } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { FormattedInput } from '@/components/ui/formatted-input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Calendar } from '@/components/ui/calendar';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import {
  CalendarIcon,
  WarningCircleIcon,
  CheckIcon,
  TrendUpIcon,
  EqualsIcon,
  SlidersIcon,
  ArrowCounterClockwiseIcon,
} from '@phosphor-icons/react';
import { format } from 'date-fns';
import { useToast } from 'src/context/ToastContext';
import {
  useCreateManualAmount,
  useUpdateManualAmount,
  ManualAmount,
} from 'src/api/rebates/useManualAmounts';
import ApiError from 'src/api/ApiError';
import { DivisionSimpleDTO, RebateRule } from '@erp/shared-types';
import { cn } from '@/lib/utils';
import {
  MultiSelect,
  MultiSelectOption,
} from '@/components/common/MultiSelect';
import type { RebateLineItem } from 'src/api/rebates/useRebateLineItems';

type SplitMode = 'even' | 'proportional' | 'custom';

/**
 * Parses a "YYYY-MM-DD" date string into a local-midnight Date.
 * Using new Date("YYYY-MM-DD") interprets the string as UTC midnight,
 * which causes off-by-one day issues in non-UTC timezones.
 */
function parseLocalDate(dateStr: string): Date {
  const [year, month, day] = dateStr.split('-').map(Number);
  return new Date(year, month - 1, day);
}

interface ManualAmountModalProps {
  open: boolean;
  onClose: () => void;
  rule: RebateRule;
  divisions: DivisionSimpleDTO[];
  manualAmount?: ManualAmount | null;
  onSuccess: () => void;
  agreementStartDate?: string;
  agreementEndDate?: string;
  /** Line items for calculating proportional splits based on transaction volume */
  lineItems?: RebateLineItem[];
}

function ManualAmountModal({
  open,
  onClose,
  rule,
  divisions,
  manualAmount,
  onSuccess,
  agreementStartDate,
  agreementEndDate,
  lineItems,
}: ManualAmountModalProps) {
  const { showToast } = useToast();
  const isEditing = !!manualAmount;

  // Form state
  const [totalAmount, setTotalAmount] = useState('');
  const [date, setDate] = useState<Date>(new Date());
  const [description, setDescription] = useState('');
  const [splitMode, setSplitMode] = useState<SplitMode>('even');
  const [selectedDivisionOptions, setSelectedDivisionOptions] = useState<
    MultiSelectOption[]
  >([]);
  const [manualAllocations, setManualAllocations] = useState<
    Record<string, string>
  >({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const createMutation = useCreateManualAmount();
  const updateMutation = useUpdateManualAmount(manualAmount?.id || '');

  // Convert divisions to MultiSelect options
  const divisionOptions: MultiSelectOption[] = useMemo(
    () =>
      divisions.map((div) => ({
        id: div.id,
        label: div.name,
        group: div.tags.length > 0 ? div.tags[0] : undefined,
      })),
    [divisions],
  );

  // Calculate default date: clamp today within agreement bounds
  // Default to min(today, endDate), but not before startDate
  const getDefaultDate = React.useCallback(() => {
    const today = new Date();
    const todayOnly = new Date(
      today.getFullYear(),
      today.getMonth(),
      today.getDate(),
    );

    if (!agreementStartDate || !agreementEndDate) return today;

    // Parse YYYY-MM-DD strings as local dates to avoid UTC timezone shift
    const startDateOnly = parseLocalDate(agreementStartDate);
    const endDateOnly = parseLocalDate(agreementEndDate);

    // Clamp today within [startDate, endDate]
    if (todayOnly < startDateOnly) return startDateOnly;
    if (todayOnly > endDateOnly) return endDateOnly;
    return today;
  }, [agreementStartDate, agreementEndDate]);

  // Initialize form when modal opens
  React.useEffect(() => {
    if (!open) return;

    if (manualAmount) {
      // Editing mode - single division with existing amount
      setTotalAmount(manualAmount.amount);
      setDate(new Date(manualAmount.date));
      setDescription(manualAmount.description);
      setSplitMode('even');
      const editingDivision = divisions.find(
        (d) => d.id === manualAmount.divisionId,
      );
      if (editingDivision) {
        setSelectedDivisionOptions([
          { id: editingDivision.id, label: editingDivision.name },
        ]);
      }
      setManualAllocations({});
      setProportionalAllocations({});
    } else {
      // Creating new - reset form with smart default date
      setTotalAmount('');
      setDate(getDefaultDate());
      setDescription('');
      setSplitMode('even');
      setSelectedDivisionOptions([]);
      setManualAllocations({});
      setProportionalAllocations({});
    }
  }, [open, manualAmount, divisions, getDefaultDate]);

  // Calculate derived values
  const selectedCount = selectedDivisionOptions.length;
  const totalAmountNum = parseFloat(totalAmount) || 0;

  // Calculate per-division amounts when even split is enabled
  const evenSplitAmount = useMemo(() => {
    if (selectedCount === 0 || totalAmountNum === 0) return 0;
    return totalAmountNum / selectedCount;
  }, [selectedCount, totalAmountNum]);

  // Calculate volume per division from line items (for proportional split)
  const divisionVolumes = useMemo(() => {
    if (!lineItems || lineItems.length === 0) {
      return new Map<string, number>();
    }

    const volumeMap = new Map<string, number>();
    for (const item of lineItems) {
      const current = volumeMap.get(item.divisionId) || 0;
      volumeMap.set(item.divisionId, current + item.value);
    }
    return volumeMap;
  }, [lineItems]);

  // Calculate proportional amounts based on transaction volume
  // Excludes divisions with zero or negative volume from ratio calculation
  const calculatedProportionalAmounts = useMemo(() => {
    if (selectedCount === 0 || totalAmountNum === 0) {
      return new Map<string, number>();
    }

    // Get total volume for selected divisions only - exclude zero/negative volumes
    let totalSelectedVolume = 0;
    for (const opt of selectedDivisionOptions) {
      const volume = divisionVolumes.get(opt.id) || 0;
      // Only include positive volumes in the ratio calculation
      if (volume > 0) {
        totalSelectedVolume += volume;
      }
    }

    const amounts = new Map<string, number>();

    if (totalSelectedVolume === 0) {
      // No positive volume data - all divisions get 0 (don't fall back to even split)
      for (const opt of selectedDivisionOptions) {
        amounts.set(opt.id, 0);
      }
    } else {
      // Calculate proportional allocation - divisions with zero/negative volume get 0
      for (const opt of selectedDivisionOptions) {
        const divisionVolume = divisionVolumes.get(opt.id) || 0;
        if (divisionVolume > 0) {
          const proportion = divisionVolume / totalSelectedVolume;
          amounts.set(opt.id, totalAmountNum * proportion);
        } else {
          // Negative or zero volume divisions get 0 allocation
          amounts.set(opt.id, 0);
        }
      }
    }

    return amounts;
  }, [selectedDivisionOptions, totalAmountNum, selectedCount, divisionVolumes]);

  // State for edited proportional allocations (allows user to modify calculated values)
  const [proportionalAllocations, setProportionalAllocations] = useState<
    Record<string, string>
  >({});

  // Track if proportional allocations have been edited
  const hasProportionalEdits = useMemo(() => {
    return Object.keys(proportionalAllocations).length > 0;
  }, [proportionalAllocations]);

  // Reset proportional allocations when calculated values change or divisions change
  const handleResetProportional = () => {
    setProportionalAllocations({});
  };

  // Get the effective proportional amount for a division (edited or calculated)
  const getProportionalAmount = (divisionId: string): number => {
    if (proportionalAllocations[divisionId] !== undefined) {
      return parseFloat(proportionalAllocations[divisionId]) || 0;
    }
    return calculatedProportionalAmounts.get(divisionId) || 0;
  };

  // Check if proportional split is available (has positive volume data for selected divisions)
  const hasVolumeDataForSelected = useMemo(() => {
    if (!lineItems || lineItems.length === 0 || selectedCount === 0) {
      return false;
    }
    // Check if at least one selected division has positive volume data
    return selectedDivisionOptions.some(
      (opt) => (divisionVolumes.get(opt.id) || 0) > 0,
    );
  }, [lineItems, selectedCount, selectedDivisionOptions, divisionVolumes]);

  // Calculate allocated total based on split mode
  const allocatedTotal = useMemo(() => {
    if (splitMode === 'even') {
      return evenSplitAmount * selectedCount;
    }
    if (splitMode === 'proportional') {
      let total = 0;
      for (const opt of selectedDivisionOptions) {
        // Inline getProportionalAmount logic to satisfy exhaustive-deps
        const amount =
          proportionalAllocations[opt.id] !== undefined
            ? parseFloat(proportionalAllocations[opt.id]) || 0
            : calculatedProportionalAmounts.get(opt.id) || 0;
        total += amount;
      }
      return total;
    }
    // Custom mode
    return selectedDivisionOptions.reduce((sum, opt) => {
      return sum + (parseFloat(manualAllocations[opt.id] || '0') || 0);
    }, 0);
  }, [
    selectedDivisionOptions,
    manualAllocations,
    splitMode,
    evenSplitAmount,
    selectedCount,
    calculatedProportionalAmounts,
    proportionalAllocations,
  ]);

  const remainingAmount = totalAmountNum - allocatedTotal;
  const isBalanced = Math.abs(remainingAmount) < 0.01;

  // Parse agreement date bounds using local date parsing to avoid UTC timezone shift
  const minDate = useMemo(() => {
    if (!agreementStartDate) return undefined;
    return parseLocalDate(agreementStartDate);
  }, [agreementStartDate]);

  const maxDate = useMemo(() => {
    if (!agreementEndDate) return undefined;
    return parseLocalDate(agreementEndDate);
  }, [agreementEndDate]);

  // Check if date is within agreement bounds
  const isDateWithinBounds = useMemo(() => {
    if (!minDate || !maxDate) return true;
    const selectedDate = new Date(
      date.getFullYear(),
      date.getMonth(),
      date.getDate(),
    );
    return selectedDate >= minDate && selectedDate <= maxDate;
  }, [date, minDate, maxDate]);

  // Validation
  const errors: {
    totalAmount?: string;
    divisions?: string;
    description?: string;
    allocations?: string;
    date?: string;
  } = {};

  if (!totalAmount) {
    errors.totalAmount = 'Total amount is required';
  } else if (totalAmountNum <= 0) {
    errors.totalAmount = 'Amount must be a positive number';
  }

  if (selectedCount === 0) {
    errors.divisions = 'Select at least one division';
  }

  if (!description.trim()) {
    errors.description = 'Description is required';
  }

  if (
    splitMode === 'custom' &&
    !isBalanced &&
    selectedCount > 0 &&
    totalAmountNum > 0
  ) {
    errors.allocations = `Allocations must equal total amount (${remainingAmount > 0 ? '+' : ''}$${remainingAmount.toFixed(2)} remaining)`;
  }

  if (!isDateWithinBounds && minDate && maxDate) {
    const formatDateForError = (d: Date) =>
      d.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      });
    errors.date = `Date must be within agreement period (${formatDateForError(minDate)} - ${formatDateForError(maxDate)})`;
  }

  const isFormValid = Object.keys(errors).length === 0;

  const handleDivisionChange = (selected: MultiSelectOption[]) => {
    setSelectedDivisionOptions(selected);
    // Clear allocations for deselected divisions
    const selectedIds = new Set(selected.map((s) => s.id));
    setManualAllocations((prev) => {
      const newAllocations: Record<string, string> = {};
      for (const id of Object.keys(prev)) {
        if (selectedIds.has(id)) {
          newAllocations[id] = prev[id];
        }
      }
      return newAllocations;
    });
    setProportionalAllocations((prev) => {
      const newAllocations: Record<string, string> = {};
      for (const id of Object.keys(prev)) {
        if (selectedIds.has(id)) {
          newAllocations[id] = prev[id];
        }
      }
      return newAllocations;
    });
  };

  const handleProportionalAllocationChange = (
    divisionId: string,
    amount: string,
  ) => {
    setProportionalAllocations((prev) => ({
      ...prev,
      [divisionId]: amount,
    }));
  };

  const handleAllocationChange = (divisionId: string, amount: string) => {
    setManualAllocations((prev) => ({
      ...prev,
      [divisionId]: amount,
    }));
  };

  const handleSubmit = async () => {
    if (!isFormValid) return;

    setIsSubmitting(true);

    try {
      // Format date as YYYY-MM-DD to avoid timezone conversion issues
      const dateToSubmit = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;

      if (isEditing) {
        // For editing, just update the single manual amount
        await updateMutation.mutateAsync({
          amount: parseFloat(totalAmount),
          date: dateToSubmit,
          description,
        });
      } else {
        // For creating, create one manual amount per selected division
        // Must be sequential because useMutationApi aborts previous requests when called in parallel
        for (const option of selectedDivisionOptions) {
          let amount: number;
          if (splitMode === 'even') {
            amount = evenSplitAmount;
          } else if (splitMode === 'proportional') {
            amount = getProportionalAmount(option.id);
          } else {
            amount = parseFloat(manualAllocations[option.id] || '0') || 0;
          }

          // Skip divisions with 0 allocation - don't create empty manual amounts
          if (amount === 0) {
            continue;
          }

          await createMutation.mutateAsync({
            ruleId: rule.id,
            divisionId: option.id,
            amount,
            date: dateToSubmit,
            description,
          });
        }
      }

      // Reset submitting state before closing
      setIsSubmitting(false);

      // Close modal and trigger success callback
      onClose();
      onSuccess();
    } catch (error) {
      setIsSubmitting(false);

      let errorMessage = 'An error occurred';

      if (error instanceof ApiError) {
        errorMessage = error.message;
      } else if (error instanceof Error) {
        errorMessage = error.message;
      } else if (typeof error === 'object' && error !== null) {
        const errorObj = error as Record<string, unknown>;
        errorMessage =
          String(errorObj.message || errorObj.error) || 'An error occurred';
      }

      showToast({
        message: errorMessage,
        title: 'Error',
        color: 'error',
        icon: 'warning',
      });
    }
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(value);
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {isEditing ? 'Edit Manual Amount' : 'Add Manual Amount'}
          </DialogTitle>
          <DialogDescription>
            {isEditing
              ? `Update the manual rebate amount for rule: ${rule.name}`
              : `Split a rebate amount across one or more divisions for rule: ${rule.name}`}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Total Amount & Date Row */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="totalAmount">Total Amount ($)</Label>
              <FormattedInput
                id="totalAmount"
                type="text"
                value={totalAmount}
                onChange={(value) => setTotalAmount(value)}
                formatType="currency"
                placeholder="0.00"
                className={cn(errors.totalAmount && 'border-destructive')}
              />
              {errors.totalAmount && (
                <p className="text-sm text-destructive">{errors.totalAmount}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label>Date</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      'w-full justify-start text-left font-normal',
                      errors.date && 'border-destructive',
                    )}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {date ? format(date, 'PPP') : 'Pick a date'}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0 z-[9999]" align="start">
                  <Calendar
                    mode="single"
                    selected={date}
                    defaultMonth={date}
                    onSelect={(d) => setDate(d || new Date())}
                    disabled={(d) => {
                      if (!minDate || !maxDate) return false;
                      const dateOnly = new Date(
                        d.getFullYear(),
                        d.getMonth(),
                        d.getDate(),
                      );
                      return dateOnly < minDate || dateOnly > maxDate;
                    }}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
              {errors.date && (
                <p className="text-sm text-destructive">{errors.date}</p>
              )}
              {minDate && maxDate && !errors.date && (
                <p className="text-sm text-muted-foreground">
                  Agreement period: {format(minDate, 'MMM d, yyyy')} -{' '}
                  {format(maxDate, 'MMM d, yyyy')}
                </p>
              )}
            </div>
          </div>

          {/* Division Selection */}
          {!isEditing && (
            <div className="space-y-2">
              <MultiSelect
                label="Divisions"
                options={divisionOptions}
                value={selectedDivisionOptions}
                onChange={handleDivisionChange}
                placeholder="Select divisions..."
                searchPlaceholder="Search divisions..."
                fullWidth
                error={!!errors.divisions}
                helperText={errors.divisions}
              />
            </div>
          )}

          {/* Split Mode Selection */}
          {!isEditing && selectedCount > 1 && (
            <div className="space-y-4">
              <Label>Split Method</Label>
              <RadioGroup
                value={splitMode}
                onValueChange={(value) => setSplitMode(value as SplitMode)}
                className="space-y-2"
              >
                {/* Even Split Option */}
                <div
                  className={cn(
                    'flex items-center gap-3 rounded-lg border p-3 cursor-pointer transition-colors',
                    splitMode === 'even'
                      ? 'border-primary bg-primary/5'
                      : 'hover:bg-accent/50',
                  )}
                  onClick={() => setSplitMode('even')}
                >
                  <RadioGroupItem value="even" id="split-even" />
                  <EqualsIcon className="h-4 w-4 text-muted-foreground" />
                  <div className="flex-1">
                    <Label
                      htmlFor="split-even"
                      className="cursor-pointer font-medium"
                    >
                      Split Evenly
                    </Label>
                    <p className="text-sm text-muted-foreground">
                      Divide equally among selected divisions
                      {totalAmountNum > 0 && selectedCount > 0 && (
                        <span className="ml-1 text-foreground font-medium">
                          ({formatCurrency(evenSplitAmount)} each)
                        </span>
                      )}
                    </p>
                  </div>
                </div>

                {/* Proportional Split Option */}
                <div
                  className={cn(
                    'flex items-center gap-3 rounded-lg border p-3 cursor-pointer transition-colors',
                    splitMode === 'proportional'
                      ? 'border-primary bg-primary/5'
                      : 'hover:bg-accent/50',
                    !lineItems || lineItems.length === 0
                      ? 'opacity-50 cursor-not-allowed'
                      : '',
                  )}
                  onClick={() => {
                    if (lineItems && lineItems.length > 0) {
                      setSplitMode('proportional');
                    }
                  }}
                >
                  <RadioGroupItem
                    value="proportional"
                    id="split-proportional"
                    disabled={!lineItems || lineItems.length === 0}
                  />
                  <TrendUpIcon className="h-4 w-4 text-muted-foreground" />
                  <div className="flex-1">
                    <Label
                      htmlFor="split-proportional"
                      className={cn(
                        'cursor-pointer font-medium',
                        !lineItems || lineItems.length === 0
                          ? 'cursor-not-allowed'
                          : '',
                      )}
                    >
                      Proportional by Volume
                    </Label>
                    <p className="text-sm text-muted-foreground">
                      {!lineItems || lineItems.length === 0
                        ? 'No transaction volume data available'
                        : hasVolumeDataForSelected
                          ? 'Based on transaction volume for each division'
                          : 'Selected divisions have no positive volume data'}
                    </p>
                  </div>
                </div>

                {/* Custom Split Option */}
                <div
                  className={cn(
                    'flex items-center gap-3 rounded-lg border p-3 cursor-pointer transition-colors',
                    splitMode === 'custom'
                      ? 'border-primary bg-primary/5'
                      : 'hover:bg-accent/50',
                  )}
                  onClick={() => setSplitMode('custom')}
                >
                  <RadioGroupItem value="custom" id="split-custom" />
                  <SlidersIcon className="h-4 w-4 text-muted-foreground" />
                  <div className="flex-1">
                    <Label
                      htmlFor="split-custom"
                      className="cursor-pointer font-medium"
                    >
                      Custom Allocation
                    </Label>
                    <p className="text-sm text-muted-foreground">
                      Manually specify amount for each division
                    </p>
                  </div>
                </div>
              </RadioGroup>

              {/* Proportional Allocation (Editable) */}
              {splitMode === 'proportional' && selectedCount > 0 && (
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <Label>Proportional Allocation</Label>
                    {hasProportionalEdits && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={handleResetProportional}
                        className="h-7 text-xs gap-1"
                      >
                        <ArrowCounterClockwiseIcon className="h-3 w-3" />
                        Reset to Calculated
                      </Button>
                    )}
                  </div>
                  <div className="border rounded-md divide-y">
                    {selectedDivisionOptions.map((option) => {
                      const volume = divisionVolumes.get(option.id) || 0;
                      const calculatedAmount =
                        calculatedProportionalAmounts.get(option.id) || 0;
                      const currentAmount = getProportionalAmount(option.id);
                      // Only count positive volumes in the total for percentage calculation
                      const totalPositiveVolume =
                        selectedDivisionOptions.reduce((sum, opt) => {
                          const v = divisionVolumes.get(opt.id) || 0;
                          return sum + (v > 0 ? v : 0);
                        }, 0);
                      const percentage =
                        totalPositiveVolume > 0 && volume > 0
                          ? (volume / totalPositiveVolume) * 100
                          : 0;
                      const isZeroVolume = volume <= 0;
                      const isEdited =
                        proportionalAllocations[option.id] !== undefined;
                      return (
                        <div
                          key={option.id}
                          className={cn(
                            'flex items-center gap-3 p-3',
                            isZeroVolume && 'bg-muted/30',
                          )}
                        >
                          <span className="flex-1 text-sm">
                            {option.label}
                            {isZeroVolume && (
                              <span className="ml-2 text-xs text-muted-foreground">
                                (no positive volume)
                              </span>
                            )}
                          </span>
                          <span className="text-xs text-muted-foreground w-16 text-right">
                            {percentage.toFixed(1)}%
                          </span>
                          <div className="w-32">
                            <FormattedInput
                              type="text"
                              value={
                                proportionalAllocations[option.id] !== undefined
                                  ? proportionalAllocations[option.id]
                                  : calculatedAmount.toFixed(2)
                              }
                              onChange={(value) =>
                                handleProportionalAllocationChange(
                                  option.id,
                                  value,
                                )
                              }
                              formatType="currency"
                              placeholder="0.00"
                              className={cn(
                                'h-8 text-sm',
                                isEdited && 'border-blue-400',
                              )}
                            />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  {!hasVolumeDataForSelected && (
                    <p className="text-sm text-amber-600">
                      No positive volume data for selected divisions - all
                      allocations set to $0
                    </p>
                  )}

                  {/* Allocation Summary for Proportional Mode */}
                  {totalAmountNum > 0 && (
                    <div
                      className={cn(
                        'flex items-center justify-between rounded-lg border p-3',
                        isBalanced
                          ? 'border-green-500/50 bg-green-50 dark:bg-green-950/20'
                          : 'border-amber-500/50 bg-amber-50 dark:bg-amber-950/20',
                      )}
                    >
                      <div className="flex items-center gap-2">
                        {isBalanced ? (
                          <CheckIcon className="h-4 w-4 text-green-600" />
                        ) : (
                          <WarningCircleIcon className="h-4 w-4 text-amber-600" />
                        )}
                        <span className="text-sm font-medium">
                          {isBalanced
                            ? 'Fully allocated'
                            : 'Allocation mismatch'}
                        </span>
                      </div>
                      <div className="text-sm">
                        <span className="text-muted-foreground">
                          Allocated:{' '}
                        </span>
                        <span className="font-medium">
                          {formatCurrency(allocatedTotal)}
                        </span>
                        <span className="text-muted-foreground"> / </span>
                        <span className="font-medium">
                          {formatCurrency(totalAmountNum)}
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Custom Allocation Inputs */}
              {splitMode === 'custom' && (
                <div className="space-y-3">
                  <Label>Custom Allocation</Label>
                  <div className="border rounded-md divide-y">
                    {selectedDivisionOptions.map((option) => (
                      <div
                        key={option.id}
                        className="flex items-center gap-3 p-3"
                      >
                        <span className="flex-1 text-sm">{option.label}</span>
                        <div className="w-32">
                          <FormattedInput
                            type="text"
                            value={manualAllocations[option.id] || ''}
                            onChange={(value) =>
                              handleAllocationChange(option.id, value)
                            }
                            formatType="currency"
                            placeholder="0.00"
                            className="h-8 text-sm"
                          />
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Allocation Summary */}
                  <div
                    className={cn(
                      'flex items-center justify-between rounded-lg border p-3',
                      isBalanced
                        ? 'border-green-500/50 bg-green-50 dark:bg-green-950/20'
                        : 'border-amber-500/50 bg-amber-50 dark:bg-amber-950/20',
                    )}
                  >
                    <div className="flex items-center gap-2">
                      {isBalanced ? (
                        <CheckIcon className="h-4 w-4 text-green-600" />
                      ) : (
                        <WarningCircleIcon className="h-4 w-4 text-amber-600" />
                      )}
                      <span className="text-sm font-medium">
                        {isBalanced ? 'Fully allocated' : 'Allocation mismatch'}
                      </span>
                    </div>
                    <div className="text-sm">
                      <span className="text-muted-foreground">Allocated: </span>
                      <span className="font-medium">
                        {formatCurrency(allocatedTotal)}
                      </span>
                      <span className="text-muted-foreground"> / </span>
                      <span className="font-medium">
                        {formatCurrency(totalAmountNum)}
                      </span>
                    </div>
                  </div>

                  {errors.allocations && (
                    <p className="text-sm text-destructive">
                      {errors.allocations}
                    </p>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Description */}
          <div className="space-y-2">
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Describe the reason for this manual rebate amount"
              rows={3}
              className={cn(errors.description && 'border-destructive')}
            />
            {errors.description ? (
              <p className="text-sm text-destructive">{errors.description}</p>
            ) : (
              <p className="text-sm text-muted-foreground">
                This description will be applied to all created manual amounts
              </p>
            )}
          </div>

          {/* Summary for create mode */}
          {!isEditing &&
            selectedCount > 0 &&
            totalAmountNum > 0 &&
            isFormValid &&
            (() => {
              // Calculate how many amounts will actually be created (excluding zeros)
              let nonZeroCount = 0;
              for (const opt of selectedDivisionOptions) {
                let amount: number;
                if (splitMode === 'even') {
                  amount = evenSplitAmount;
                } else if (splitMode === 'proportional') {
                  amount = getProportionalAmount(opt.id);
                } else {
                  amount = parseFloat(manualAllocations[opt.id] || '0') || 0;
                }
                if (amount !== 0) {
                  nonZeroCount++;
                }
              }
              const skippedCount = selectedCount - nonZeroCount;

              if (nonZeroCount === 0) {
                return (
                  <div className="rounded-lg bg-amber-50 border border-amber-200 p-4">
                    <p className="text-sm text-amber-700">
                      No manual amounts will be created - all allocations are $0
                    </p>
                  </div>
                );
              }

              return (
                <div className="rounded-lg bg-muted/50 p-4">
                  <h4 className="text-sm font-medium mb-2">Summary</h4>
                  <p className="text-sm text-muted-foreground">
                    {nonZeroCount === 1 ? (
                      <>
                        Creating 1 manual amount of{' '}
                        <span className="font-medium text-foreground">
                          {formatCurrency(allocatedTotal)}
                        </span>
                      </>
                    ) : (
                      <>
                        Creating {nonZeroCount} manual amounts totaling{' '}
                        <span className="font-medium text-foreground">
                          {formatCurrency(allocatedTotal)}
                        </span>
                        {splitMode === 'even' && (
                          <> ({formatCurrency(evenSplitAmount)} each)</>
                        )}
                        {splitMode === 'proportional' && (
                          <> (proportional to transaction volume)</>
                        )}
                      </>
                    )}
                    {skippedCount > 0 && (
                      <span className="block mt-1 text-xs text-muted-foreground">
                        {skippedCount} division{skippedCount !== 1 ? 's' : ''}{' '}
                        with $0 allocation will be skipped
                      </span>
                    )}
                  </p>
                </div>
              );
            })()}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!isFormValid || isSubmitting}
            className="min-w-24"
          >
            {(() => {
              if (isSubmitting) return 'Saving...';
              if (isEditing) return 'Update';

              // Calculate non-zero count for button text
              let nonZeroCount = 0;
              for (const opt of selectedDivisionOptions) {
                let amount: number;
                if (splitMode === 'even') {
                  amount = evenSplitAmount;
                } else if (splitMode === 'proportional') {
                  amount = getProportionalAmount(opt.id);
                } else {
                  amount = parseFloat(manualAllocations[opt.id] || '0') || 0;
                }
                if (amount !== 0) {
                  nonZeroCount++;
                }
              }

              if (nonZeroCount === 0) return 'Create';
              if (nonZeroCount === 1) return 'Create';
              return `Create ${nonZeroCount} Amounts`;
            })()}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default ManualAmountModal;
